
import { WordPair } from "@/types/game";
import { debugLog, DebugLevel } from "@/utils/debugUtils";
import { calculateAndUpdateProximity } from "@/utils/proximityUtils";
import { ensureNextRoundExists } from "./databaseOperations";
import { handleUiFocus, handleRoundPreparationError } from "./uiHandlers";
import { captureRoundPreparationState } from "./stateManagement";
import { generateNextAiWord } from "./aiWordGeneration";

/**
 * Orchestrates the preparation for the next round
 */
export const prepareNextRound = async (
  gameId: string,
  currentRoundNumber: number,
  userWord: string,
  aiWord: string,
  allUsedWords: string[],
  setRounds: (rounds: WordPair[] | ((prev: WordPair[]) => WordPair[])) => void,
  setBotIsThinking: (isThinking: boolean) => void,
  setBotHasChosenWord: (hasChosen: boolean) => void,
  setShouldAutoFocus: (shouldAutoFocus: boolean) => void,
  setProcessingComplete: (complete: boolean) => void,
  toast: any
) => {
  // Log the start of round preparation
  debugLog(DebugLevel.INFO, "roundPreparation", `Preparing next round (${currentRoundNumber + 1})`);
  
  // Capture the initial state
  captureRoundPreparationState(
    "start", 
    gameId, 
    currentRoundNumber + 1,
    userWord,
    aiWord,
    false, // userHasSubmitted
    false, // botHasChosenWord
    false  // botIsThinking
  );
  
  try {
    // Step 1: Create next round in the database if needed
    await ensureNextRoundExists(gameId, currentRoundNumber + 1);
    
    // Step 2: Handle UI focus behavior
    handleUiFocus(setShouldAutoFocus);
    
    // Step 3: Calculate proximity for the current round
    try {
      debugLog(DebugLevel.INFO, "roundPreparation", "Calculating proximity for current round", { roundNumber: currentRoundNumber, userWord, aiWord });
      await calculateAndUpdateProximity(
        gameId,
        currentRoundNumber,
        userWord,
        aiWord,
        setRounds
      );
      
      // Add a small delay before the next Claude API call to prevent overloading
      debugLog(DebugLevel.INFO, "roundPreparation", "Waiting 1 second before generating AI word");
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (proximityError) {
      debugLog(DebugLevel.ERROR, "roundPreparation", "Error calculating proximity", proximityError);
      handleRoundPreparationError('proximity', toast);
      // Continue with AI word generation even if proximity calculation fails
    }
    
    // Step 4: Generate the bot's word for the next round
    const aiWordResult = await generateNextAiWord(
      gameId,
      currentRoundNumber,
      userWord,
      aiWord,
      allUsedWords,
      setBotIsThinking,
      setBotHasChosenWord,
      setRounds,
      toast
    );
    
    // Fall through to finally block to complete processing
  } catch (error) {
    debugLog(DebugLevel.ERROR, "roundPreparation", "Unhandled error in prepareNextRound", error);
    handleRoundPreparationError('general', toast);
  } finally {
    // Always make sure to reset the thinking state and mark processing as complete
    setBotIsThinking(false);
    debugLog(DebugLevel.INFO, "roundPreparation", "Set botIsThinking to false (finally block)");
    setProcessingComplete(true);
    debugLog(DebugLevel.INFO, "roundPreparation", "Set processingComplete to true (finally block)");
  }
};
